import * as Devices from './model';
import { DeviceTypes, Statuses } from '.';
import * as DTHelper from '../datatable/utils';
import { AppKeys, newMsg } from '../../core/localization';
import { GroupNames, Groups } from '../../models';

export const msgTitle = newMsg(GroupNames[Groups.DEVICES]);
export const msgSearchError = newMsg(AppKeys.Devices_Search_Error);
export const msgSN = newMsg(AppKeys.Device_Prop_SeriaNumber);
export const msgKId = newMsg(AppKeys.Device_Prop_KamstrupId);
export const msgNumericalId = newMsg(AppKeys.Device_Prop_NumericalIdentity);
export const msgCDMN = newMsg(AppKeys.Device_Prop_CDMN);
export const msgOrder = newMsg(AppKeys.Device_Prop_OrderId);
export const msgStatus = newMsg(AppKeys.Device_Prop_IsActive);
export const msgStatusValue = (isActive: boolean) =>
  isActive
    ? newMsg(AppKeys.Device_Status_Active)
    : newMsg(AppKeys.Device_Status_Removed);
export const msgStatusActive = newMsg(AppKeys.sidebar_devices_status_active);
export const msgStatusRemoved = newMsg(AppKeys.sidebar_devices_status_removed);
export const msgDeviceType = newMsg(AppKeys.Device_Prop_DeviceType);
export const msgDeviceName = newMsg(AppKeys.Device_Prop_DeviceName);
export const msgOwner = newMsg(AppKeys.Device_Prop_Owner);
export const msgReadRights = newMsg(AppKeys.Device_Prop_EditRights);
export const msgSearchViaFileBtn = newMsg(
  AppKeys.sidebar_devices_searchViaFile
);
export const keyTotalDevices = AppKeys.Num_Device;

export const searchVFileHeader = newMsg(AppKeys.Dialog_SVF_Header);
export const searchVFileDesc = newMsg(AppKeys.Dialog_SVF_Desc);
export const searchVFileSample = newMsg(AppKeys.Dialog_SVF_Sample);
export const searchVFileHere = newMsg(AppKeys.Com_Here);
export const searchVFileApplyBtn = newMsg(AppKeys.Btn_Apply);

export const getPK = (o: Devices.PKey): string =>
  `${o.manufactureId}${o.serialNumber}`;

export const getSNLabel = (o: Devices.Identifiers): string => 
    o.serialPresentationLabel ?? getPK(o);

export const getItemByPK = <T extends Devices.PKey>(items: T[], key: string) =>
  items.find((i) => getPK(i) === key);

export const getItemById = <T extends Devices.PKey>(
  items: T[],
  deviceId: Devices.PKey
) => getItemByPK(items, getPK(deviceId));

export const areEqual = (d1: Devices.PKey, d2: Devices.PKey) =>
  getPK(d1) === getPK(d2);

export const isDevicePKeyWellFormated = (key: string) =>
  /^[a-zA-Z]{3}\d{7,10}$/.test(key);

export const PK_MinLength = 10;

export const parsePK = (key: string): Devices.PKey | undefined => {
  const manId = key.split(/\d/)[0];
  const manufactureId = manId.trim();
  const serialNumber = key.slice(manId.length).trim();
  return key.length > 0 ? { manufactureId, serialNumber } : undefined;
};

export const parseKeys = (keys: string[]) =>
  keys.reduce((prev, k) => {
    const deviceId = parsePK(k);
    if (deviceId) prev.push(deviceId);
    return prev;
  }, [] as Devices.PKey[]);

export const getSelectedDevices = (
  loadedDevices: Devices.Device[],
  selectedKeys: string[]
) => loadedDevices.filter((d) => selectedKeys.includes(getPK(d)));

export const replaceItemByPK = <T extends Devices.PKey>(
  items: T[],
  newItem: T
) => {
  const oldItem = items.find((p) => getPK(p) === getPK(newItem));
  const idx = oldItem ? items.indexOf(oldItem) : -1;
  return idx > -1
    ? [...items.slice(0, idx), newItem, ...items.slice(idx + 1)]
    : [...items, newItem];
};

export const displayOrderNo = (device: Devices.Device) =>
  device.showOrderNumber ? device.orderNumber : 'N/A';

export const allDevicesOfAType = (
  devices: Devices.Device[],
  deviceType: number
) => devices.filter((i) => i.deviceType === deviceType);

export const getLoadedDevices = DTHelper.concatPageItems;

export const getTotalsFromSelectedKeys = (
  selectedKeys: string[],
  data: Devices.Data
) => {
  const devices = getSelectedDevices(
    getLoadedDevices(data.pages),
    selectedKeys
  );
  return {
    totalConcentrators: allDevicesOfAType(devices, DeviceTypes.CONCENTRATORS)
      .length,
    // totalFlowSensors: allDevicesOfAType(devices, DeviceTypes.FLOWSENSORS).length,
    totalGateways: allDevicesOfAType(devices, DeviceTypes.GATEWAYS).length,
    totalMeters: allDevicesOfAType(devices, DeviceTypes.METERS).length,
    totalRepeaters: allDevicesOfAType(devices, DeviceTypes.REPEATERS).length,
    totalValves: allDevicesOfAType(devices, DeviceTypes.VALVES).length,
  } as Devices.TotalsByType;
};
export const totalDevices = (totals: Devices.TotalsByType) =>
  Object.keys(totals).reduce(
    (prev: number, key: string) => prev + totals[key],
    0
  );
export const numMetersDevices = (totals: Devices.TotalsByType) =>
  totals.totalMeters;
export const numNoMetersDevices = (totals: Devices.TotalsByType) =>
  totalDevices(totals) - numMetersDevices(totals);

export const getStatus = (inclActive: boolean, inclRemoved: boolean) =>
  inclActive === inclRemoved
    ? Statuses.All
    : inclRemoved
    ? Statuses.Removed
    : Statuses.Active;

export const includeActives = (status: Statuses) =>
  status === Statuses.All || status === Statuses.Active;

export const includeRemoved = (status: Statuses) =>
  status === Statuses.All || status === Statuses.Removed;

export const parseInputDevicesCSV = (file: string): string => {
  const rows = file.split('\n').slice(1);
  return rows
    .map((r) => r.split(',')[0].trim())
    .filter((sn) => sn.length > 0)
    .join(',');
};
